/**
 * Rotation-aware "do these two objects touch" test.
 *
 * THE primitive for checking whether two positioned + rotated objects
 * (center / dimensions / rotation.y) touch or nearly touch. Never write a
 * world-axis AABB test for this: an object's dimensions are LOCAL extents,
 * so once rotation.y is non-cardinal (or swaps x/z at ±90°) a world-axis
 * comparison tests the wrong axes. That exact bug deleted a 3.30m rotated
 * double-vanity cabinet as "standalone storage" because its embedded sinks
 * read as not touching (structure B7D88301).
 */

const { doPolygonsOverlap } = require('../bathroom-analyzer/pipeline/geometry');
const { footprintFor } = require('./objectFootprint');

const DEFAULT_DIMENSION_M = 0.5;

const dimOrDefault = (value) => (value == null ? DEFAULT_DIMENSION_M : value);

/**
 * Plan-view footprint corners, padded so each face moves out by `pad / 2`.
 */
function inflatedFootprint(obj, pad) {
  const center = obj?.center || { x: 0, z: 0 };
  const dims = obj?.dimensions;
  return footprintFor(
    { x: center.x ?? 0, z: center.z ?? 0 },
    dimOrDefault(dims?.x) + pad,
    dimOrDefault(dims?.z) + pad,
    obj?.rotation?.y ?? 0
  );
}

/**
 * True when two objects' rotated footprints overlap in plan view (within
 * `tolerance` of face-to-face gap) AND their vertical intervals overlap
 * (same tolerance). Rotation about Y never affects the vertical extent.
 *
 * @param {Object} obj1 - { center, dimensions, rotation }
 * @param {Object} obj2 - { center, dimensions, rotation }
 * @param {number} [tolerance=0.05] - Allowed gap in meters
 * @returns {boolean}
 */
function objectsAreTouching(obj1, obj2, tolerance = 0.05) {
  const y1 = obj1?.center?.y ?? 0;
  const y2 = obj2?.center?.y ?? 0;
  const halfH1 = dimOrDefault(obj1?.dimensions?.y) / 2;
  const halfH2 = dimOrDefault(obj2?.dimensions?.y) / 2;
  if (Math.abs(y1 - y2) > halfH1 + halfH2 + tolerance) return false;

  return doPolygonsOverlap(
    inflatedFootprint(obj1, tolerance),
    inflatedFootprint(obj2, tolerance)
  );
}

module.exports = { objectsAreTouching };
